import React, { useState } from "react";
import { ArrowUpRight, Clock3 } from "lucide-react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

import image1 from "../../../Assets/Images/image1.png";
import image2 from "../../../Assets/Images/image2.png";
import WavyText from "../../../components/animations/WavyText.jsx";
import ContactModal from "../../../Assets/Common/ContactModal.jsx";

export default function Hero() {
  const navigate = useNavigate();
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);


  const mainServices = [
    {
      id: 1,
      title: "Software Development",
      description: "Custom software, web & mobile solutions",
      path: "/services/software-development",
      available: true,
    },
    {
      id: 2,
      title: "API Services",
      description: "Business messaging & automation solutions",
      path: "/services/api",
      available: true,
    },
    {
      id: 3,
      title: "Legal Services",
      description: "Registration, compliance & legal solutions",
      path: "/services/legal-services",
      available: true,
    },
    {
      id: 4,
      title: "Product Development",
      description: "Building innovative digital products",
      path: null,
      available: false,
    },
  ];

  /* =========================================================
     SERVICE CLICK
  ========================================================== */

  const handleServiceClick = (service) => {
    if (!service.available || !service.path) return;

    navigate(service.path);
  };

  /* =========================================================
     OPEN CONTACT MODAL
  ========================================================== */

  const handleOpenContactModal = () => {
    setIsContactModalOpen(true);
  };

  /* =========================================================
     CLOSE CONTACT MODAL
  ========================================================== */

  const handleCloseContactModal = () => {
    setIsContactModalOpen(false);
  };

  return (
    <>
      {/* =======================================================
          HERO SECTION
      ======================================================== */}

      <section
        className="
          relative
          isolate
          overflow-hidden
          theme-bg-primary
          theme-text-primary
          transition-colors
          duration-500
          font-saira

          pt-[7.25rem]

          sm:pt-[7.5rem]

          md:pt-[7.75rem]

          lg:pt-10

          xl:pt-12
        "
      >
        {/* =====================================================
            BACKGROUND ATMOSPHERE
        ====================================================== */}

        {/* Main orange ambient glow */}
        <motion.div
          aria-hidden="true"
          className="
            pointer-events-none
            absolute
            -right-24
            -top-24
            z-0
            h-[15rem]
            w-[15rem]
            rounded-full
            bg-primaryColor/[0.07]
            blur-[75px]

            sm:-right-32
            sm:-top-32
            sm:h-[23rem]
            sm:w-[23rem]

            md:h-[27rem]
            md:w-[27rem]

            lg:-right-40
            lg:-top-40
            lg:h-[31rem]
            lg:w-[31rem]

            xl:h-[35rem]
            xl:w-[35rem]
          "
          animate={{
            x: [0, -15, 0],
            y: [0, 25, 0],
            scale: [1, 1.06, 1],
          }}
          transition={{
            duration: 14,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          style={{
            willChange: "transform",
            transform: "translateZ(0)",
          }}
        />

        {/* Top-right gradient */}
        <div
          aria-hidden="true"
          className="
            pointer-events-none
            absolute
            right-0
            top-0
            z-0
            h-[42%]
            w-[85%]

            bg-gradient-to-bl
            from-primaryColor/[0.045]
            via-primaryColor/[0.012]
            to-transparent

            sm:h-[48%]
            sm:w-[75%]

            lg:h-[55%]
            lg:w-[65%]
          "
        />

        {/* Center atmospheric glow */}
        <div
          aria-hidden="true"
          className="
            pointer-events-none
            absolute
            left-1/2
            top-[52%]
            z-0
            h-[14rem]
            w-[22rem]
            -translate-x-1/2
            rounded-full
            bg-primaryColor/[0.018]
            blur-[90px]

            sm:h-[18rem]
            sm:w-[34rem]

            md:h-[21rem]
            md:w-[42rem]

            lg:h-[24rem]
            lg:w-[50rem]
          "
        />

        {/* Subtle radial glow */}
        <div
          aria-hidden="true"
          className="
            pointer-events-none
            absolute
            left-1/2
            top-[68%]
            z-0
            h-[16rem]
            w-[90%]
            -translate-x-1/2
            rounded-full

            bg-[radial-gradient(ellipse_at_center,rgba(255,128,0,0.025),transparent_65%)]

            sm:h-[19rem]

            lg:h-[22rem]
            lg:w-[60rem]
          "
        />

        {/* =====================================================
            MAIN CONTENT
        ====================================================== */}

        <div
          className="
            relative
            z-10
            mx-auto
            w-full
            max-w-[1700px]

            px-3
            pb-7
            pt-3

            sm:px-5
            sm:pb-9
            sm:pt-4

            md:px-7
            md:pb-10
            md:pt-5

            lg:px-10
            lg:pb-12
            lg:pt-11

            xl:px-12
            xl:pb-14
            xl:pt-16

            2xl:px-16
          "
        >
          {/* ===================================================
              HERO HEADING
          ==================================================== */}

          <motion.h1
            initial={{
              opacity: 0,
              y: 28,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.95,
              ease: [0.22, 1, 0.36, 1],
            }}
            className="
              relative
              mx-auto
              w-full
              text-center
              font-semibold
              tracking-[-0.045em]
              theme-text-primary

              text-[1.8rem]
              leading-[1.15]

              sm:text-[2.3rem]
              sm:leading-[1.12]

              md:text-[2.9rem]
              md:leading-[1.08]

              lg:max-w-[1120px]
              lg:text-[clamp(3.5rem,5.2vw,5rem)]
              lg:leading-[1.05]

              xl:text-[clamp(4rem,5vw,5.2rem)]
            "
          >
            {/* Line 1 */}
            <span
              className="
                flex
                items-center
                justify-center
                gap-1
                whitespace-nowrap

                sm:gap-1.5
                md:gap-2.5
                lg:gap-3.5
              "
            >
              <WavyText text="Powering" />

              <motion.img
                src={image1}
                alt="Innovation"
                loading="eager"
                className="
                  h-[1.25rem]
                  w-[1.25rem]
                  shrink-0
                  object-contain

                  sm:h-[1.65rem]
                  sm:w-[1.65rem]

                  md:h-[2.35rem]
                  md:w-[2.35rem]

                  lg:h-[3rem]
                  lg:w-[3rem]

                  xl:h-[3.3rem]
                  xl:w-[3.3rem]
                "
                animate={{
                  scale: [1, 1.07, 1],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                style={{
                  willChange: "transform",
                  transform: "translateZ(0)",
                }}
              />

              <WavyText text="Innovations" />
            </span>

            {/* Line 2 */}
            <span
              className="
                mt-1
                block
                whitespace-nowrap

                sm:mt-1.5
                md:mt-2
                lg:mt-2
              "
            >
              <WavyText text="Empower Tailor Software" />
            </span>

            {/* Line 3 */}
            <span
              className="
                mt-1
                flex
                items-center
                justify-center
                gap-1
                whitespace-nowrap

                sm:mt-1.5
                sm:gap-1.5

                md:mt-2
                md:gap-2.5

                lg:mt-2
                lg:gap-3
              "
            >
              <motion.img
                src={image2}
                alt="Development"
                loading="lazy"
                className="
                  h-[1.25rem]
                  w-[1.25rem]
                  shrink-0
                  object-contain

                  sm:h-[1.65rem]
                  sm:w-[1.65rem]

                  md:h-[2.25rem]
                  md:w-[2.25rem]

                  lg:h-[2.9rem]
                  lg:w-[2.9rem]

                  xl:h-[3.2rem]
                  xl:w-[3.2rem]
                "
                animate={{
                  rotate: 360,
                }}
                transition={{
                  duration: 18,
                  repeat: Infinity,
                  ease: "linear",
                }}
                style={{
                  willChange: "transform",
                  transform: "translateZ(0)",
                }}
              />

              <WavyText text="Development." />
            </span>
          </motion.h1>

          {/* ===================================================
              SERVICES
          ==================================================== */}

          <div
            className="
              mx-auto
              mt-6
              w-full

              sm:mt-7
              md:mt-8
              lg:mt-8
            "
          >
            {/* Services label */}
            <motion.div
              initial={{
                opacity: 0,
                y: 10,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                delay: 0.35,
                duration: 0.65,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="flex justify-center px-2"
            >
              <div
                className="
                  inline-flex
                  max-w-full
                  items-center
                  gap-1.5
                  rounded-full
                  border
                  theme-border
                  theme-bg-secondary
                  px-2.5
                  py-1.5
                  backdrop-blur-sm

                  sm:gap-2.5
                  sm:px-3.5

                  md:gap-3
                  md:px-4

                  lg:px-5
                  lg:py-2
                "
              >
                <span
                  className="
                    h-px
                    w-3
                    shrink-0
                    bg-gradient-to-r
                    from-transparent
                    to-primaryColor/60

                    sm:w-6
                    md:w-9
                    lg:w-12
                  "
                />

                <span
                  className="
                    relative
                    flex
                    h-1.5
                    w-1.5
                    shrink-0
                    items-center
                    justify-center

                    sm:h-2
                    sm:w-2
                  "
                >
                  <span
                    className="
                      absolute
                      h-4
                      w-4
                      rounded-full
                      bg-primaryColor/15
                      blur-md
                    "
                  />

                  <span
                    className="
                      relative
                      h-1
                      w-1
                      rounded-full
                      bg-primaryColor
                      shadow-[0_0_12px_rgba(255,128,0,0.7)]

                      sm:h-1.5
                      sm:w-1.5
                    "
                  />
                </span>

                <span
                  className="
                    whitespace-nowrap
                    text-[6.5px]
                    font-semibold
                    uppercase
                    tracking-[0.16em]
                    theme-text-muted

                    sm:text-[7px]
                    sm:tracking-[0.22em]

                    md:text-[8px]

                    lg:text-[9px]
                    lg:tracking-[0.28em]
                  "
                >
                  What We Offer
                </span>

                <span
                  className="
                    h-px
                    w-3
                    shrink-0
                    bg-gradient-to-l
                    from-transparent
                    to-primaryColor/60

                    sm:w-6
                    md:w-9
                    lg:w-12
                  "
                />
              </div>
            </motion.div>

            {/* =================================================
                SERVICE GRID
            ================================================== */}

            <motion.div
              initial={{
                opacity: 0,
                y: 18,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                delay: 0.42,
                duration: 0.7,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="
                relative
                mx-auto
                mt-3
                w-full
                max-w-[1250px]

                sm:mt-4
                md:mt-5
                lg:mt-6
              "
            >
              {/* Group glow */}
              <div
                aria-hidden="true"
                className="
                  pointer-events-none
                  absolute
                  left-1/2
                  top-1/2
                  h-[85%]
                  w-[95%]
                  -translate-x-1/2
                  -translate-y-1/2
                  rounded-full
                  bg-primaryColor/[0.018]
                  blur-[45px]

                  sm:w-[90%]

                  lg:w-[85%]
                  lg:blur-[65px]
                "
              />

              {/* Grid */}
              <div
                className="
                  relative
                  grid
                  grid-cols-2
                  gap-2

                  sm:gap-2.5
                  md:gap-3

                  lg:grid-cols-4
                  lg:gap-4
                "
              >
                {mainServices.map((service, index) => (
                  <motion.button
                    key={service.id}
                    type="button"
                    disabled={!service.available}
                    onClick={() => handleServiceClick(service)}
                    initial={{
                      opacity: 0,
                      y: 18,
                    }}
                    animate={{
                      opacity: 1,
                      y: 0,
                    }}
                    transition={{
                      delay: 0.48 + index * 0.08,
                      duration: 0.5,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                    whileHover={
                      service.available
                        ? {
                            y: -4,
                            scale: 1.01,
                          }
                        : {
                            y: -2,
                          }
                    }
                    whileTap={
                      service.available
                        ? {
                            scale: 0.985,
                          }
                        : undefined
                    }
                    className="
                      group
                      relative
                      min-w-0
                      min-h-[108px]
                      overflow-hidden
                      rounded-[13px]
                      border
                      theme-border
                      theme-card
                      text-left
                      outline-none
                      transition-all
                      duration-500

                      sm:min-h-[114px]
                      sm:rounded-[15px]

                      md:min-h-[120px]
                      md:rounded-[16px]

                      lg:min-h-[128px]
                      lg:rounded-[18px]

                      xl:min-h-[132px]

                      cursor-pointer

                      shadow-[0_10px_30px_var(--shadow-color)]

                      hover:border-primaryColor/35
                      hover:shadow-[0_18px_40px_var(--shadow-color)]

                      focus-visible:border-primaryColor/50
                      focus-visible:ring-2
                      focus-visible:ring-primaryColor/20

                      disabled:cursor-default
                    "
                    style={{
                      willChange: "transform",
                      backgroundImage: `
                        linear-gradient(
                          135deg,
                          color-mix(
                            in srgb,
                            var(--brand-primary) 5.5%,
                            var(--card-bg)
                          ),
                          var(--card-bg),
                          var(--bg-tertiary)
                        )
                      `,
                    }}
                  >
                    {/* Floating effect */}
                    <motion.div
                      aria-hidden="true"
                      className="pointer-events-none absolute inset-0"
                      animate={{
                        y: [0, -1.5, 0, 1.5, 0],
                      }}
                      transition={{
                        duration: 7 + index * 0.7,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: index * 0.45,
                      }}
                    />

                    {/* Top light */}
                    <div
                      aria-hidden="true"
                      className="
                        pointer-events-none
                        absolute
                        left-2.5
                        right-2.5
                        top-0
                        h-px
                        bg-gradient-to-r
                        from-transparent
                        via-[var(--border-primary)]
                        to-transparent
                        transition-all
                        duration-500
                        group-hover:via-primaryColor/60

                        sm:left-3
                        sm:right-3

                        md:left-4
                        md:right-4
                      "
                    />

                    {/* Top-right glow */}
                    <motion.div
                      aria-hidden="true"
                      className="
                        pointer-events-none
                        absolute
                        -right-8
                        -top-8
                        h-20
                        w-20
                        rounded-full
                        bg-primaryColor/10
                        opacity-0
                        blur-3xl
                        transition-opacity
                        duration-500
                        group-hover:opacity-100

                        sm:-right-10
                        sm:-top-10
                        sm:h-24
                        sm:w-24

                        lg:h-28
                        lg:w-28
                      "
                    />

                    {/* Bottom-left glow */}
                    <div
                      aria-hidden="true"
                      className="
                        pointer-events-none
                        absolute
                        -bottom-10
                        -left-6
                        h-16
                        w-16
                        rounded-full
                        bg-primaryColor/[0.06]
                        opacity-0
                        blur-3xl
                        transition-opacity
                        duration-500
                        group-hover:opacity-100

                        sm:h-20
                        sm:w-20

                        lg:h-24
                        lg:w-24
                      "
                    />

                    {/* Shine */}
                    <div
                      aria-hidden="true"
                      className="
                        pointer-events-none
                        absolute
                        -left-[125%]
                        top-[-30%]
                        h-[160%]
                        w-[55%]
                        rotate-[20deg]
                        bg-gradient-to-r
                        from-transparent
                        via-[var(--border-secondary)]
                        to-transparent
                        transition-all
                        duration-[1000ms]
                        ease-out
                        group-hover:left-[145%]
                      "
                    />

                    {/* Card content */}
                    <div
                      className="
                        relative
                        z-10
                        flex
                        h-full
                        min-h-[108px]
                        flex-col
                        p-2.5

                        sm:min-h-[114px]
                        sm:p-3

                        md:min-h-[120px]
                        md:p-3.5

                        lg:min-h-[128px]
                        lg:p-4

                        xl:min-h-[132px]
                      "
                    >
                      {/* Action */}
                      <div className="flex w-full justify-end">
                        {service.available ? (
                          <span
                            className="
                              flex
                              h-6
                              w-6
                              shrink-0
                              items-center
                              justify-center
                              rounded-full
                              border
                              theme-border
                              theme-bg-secondary
                              theme-text-muted
                              shadow-[0_4px_12px_var(--shadow-color)]
                              transition-all
                              duration-500

                              group-hover:border-primaryColor/60
                              group-hover:bg-primaryColor
                              group-hover:text-black
                              group-hover:shadow-[0_0_18px_rgba(255,128,0,0.25)]

                              sm:h-7
                              sm:w-7

                              md:h-8
                              md:w-8

                              lg:h-8
                              lg:w-8
                            "
                          >
                            <ArrowUpRight
                              size={11}
                              strokeWidth={2}
                              className="
                                transition-transform
                                duration-500
                                group-hover:rotate-45

                                sm:h-3
                                sm:w-3

                                md:h-3.5
                                md:w-3.5

                                lg:h-4
                                lg:w-4
                              "
                            />
                          </span>
                        ) : (
                          <span
                            className="
                              inline-flex
                              shrink-0
                              items-center
                              gap-1
                              rounded-full
                              border
                              border-primaryColor/30
                              bg-primaryColor/[0.08]
                              px-2
                              py-1
                              text-[6px]
                              font-semibold
                              uppercase
                              tracking-[0.08em]
                              text-primaryColor

                              sm:px-2.5
                              sm:text-[7px]

                              md:text-[8px]

                              lg:px-3
                              lg:text-[9px]
                            "
                          >
                            <Clock3
                              size={7}
                              className="
                                sm:h-2.5
                                sm:w-2.5

                                lg:h-3
                                lg:w-3
                              "
                            />

                            <span>Soon</span>
                          </span>
                        )}
                      </div>

                      {/* Service content */}
                      <div
                        className="
                          flex
                          flex-1
                          min-w-0
                          flex-col
                          items-center
                          justify-center
                          px-1
                          text-center

                          sm:px-1.5
                          md:px-2
                          lg:px-3
                        "
                      >
                        <h3
                          className="
                            max-w-full
                            break-words
                            text-center
                            text-[12px]
                            font-semibold
                            leading-[1.2]
                            tracking-[-0.015em]
                            theme-text-primary
                            transition-all
                            duration-300
                            group-hover:-translate-y-[1px]
                            group-hover:text-primaryColor

                            sm:text-[13px]
                            md:text-[15px]
                            lg:text-[16px]
                            xl:text-[17px]
                          "
                        >
                          {service.title}
                        </h3>

                        <p
                          className="
                            mt-1
                            max-w-[95%]
                            break-words
                            text-center
                            text-[8px]
                            font-medium
                            leading-[1.4]
                            theme-text-secondary

                            sm:mt-1.5
                            sm:text-[9px]

                            md:text-[10px]

                            lg:text-[11px]
                            xl:text-[12px]
                          "
                        >
                          {service.description}
                        </p>
                      </div>
                    </div>

                    {/* Corner detail */}
                    <div
                      aria-hidden="true"
                      className="
                        pointer-events-none
                        absolute
                        bottom-0
                        right-0
                        h-6
                        w-6
                        rounded-tl-[13px]
                        border-l
                        border-t
                        theme-border
                        transition-all
                        duration-500
                        group-hover:border-primaryColor/20

                        sm:h-7
                        sm:w-7

                        md:h-8
                        md:w-8

                        lg:h-9
                        lg:w-9
                      "
                    />

                    {/* Bottom orange accent */}
                    <div
                      aria-hidden="true"
                      className="
                        absolute
                        bottom-0
                        left-0
                        h-[2px]
                        w-0
                        bg-primaryColor
                        shadow-[0_0_14px_rgba(255,128,0,0.6)]
                        transition-all
                        duration-500
                        group-hover:w-full
                      "
                    />

                    <div
                      aria-hidden="true"
                      className="
                        pointer-events-none
                        absolute
                        bottom-0
                        left-0
                        h-px
                        w-full
                        bg-gradient-to-r
                        from-primaryColor/15
                        via-transparent
                        to-transparent
                      "
                    />
                  </motion.button>
                ))}
              </div>
            </motion.div>

            {/* =================================================
                SERVICE NOTE
            ================================================== */}

            <motion.div
              initial={{
                opacity: 0,
                y: 7,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                delay: 0.78,
                duration: 0.55,
              }}
              className="
                mt-3
                flex
                justify-center
                px-3

                sm:mt-4
                sm:px-4

                md:mt-5
              "
            >
              <p
                className="
                  max-w-[600px]
                  text-center
                  text-[6.5px]
                  font-medium
                  leading-relaxed
                  tracking-[0.05em]
                  theme-text-muted

                  sm:text-[8px]
                  sm:tracking-[0.07em]

                  md:text-[9px]
                  md:tracking-[0.09em]
                "
              >
                Technology, communication & compliance — built around your
                needs.
              </p>
            </motion.div>

            {/* =================================================
                CTA
            ================================================== */}

            <motion.div
              initial={{
                opacity: 0,
                y: 10,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                delay: 0.83,
                duration: 0.6,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="
                mt-3
                flex
                justify-center
                px-3

                sm:mt-4
                sm:px-4

                md:mt-5
              "
            >
              <motion.button
                type="button"

                /* ============================================
                   OPEN CONTACT MODAL
                ============================================ */
                onClick={handleOpenContactModal}

                whileHover={{
                  scale: 1.035,
                }}
                whileTap={{
                  scale: 0.97,
                }}
                className="
                  group
                  relative
                  max-w-full
                  overflow-hidden
                  rounded-full
                  bg-primaryColor
                  px-3
                  py-1.5
                  font-semibold
                  text-black
                  shadow-[0_8px_25px_rgba(255,128,0,0.15)]
                  transition-all
                  duration-300

                  hover:shadow-[0_12px_35px_rgba(255,128,0,0.28)]

                  sm:px-4
                  sm:py-2

                  md:px-5
                  md:py-2.5

                  lg:px-6
                  lg:py-2.5
                "
              >
                {/* Hover background */}
                <span
                  aria-hidden="true"
                  className="
                    absolute
                    inset-0
                    w-0
                    bg-black
                    transition-all
                    duration-500
                    group-hover:w-full
                  "
                />

                {/* CTA content */}
                <span
                  className="
                    relative
                    z-10
                    flex
                    items-center
                    gap-1.5
                    whitespace-nowrap
                    text-[8px]
                    transition-colors
                    duration-300
                    group-hover:text-white

                    sm:gap-2
                    sm:text-[10px]

                    md:text-xs

                    lg:text-sm
                  "
                >
                  Let's Build Together

                  <span
                    className="
                      flex
                      h-4
                      w-4
                      shrink-0
                      items-center
                      justify-center
                      rounded-full
                      bg-black
                      text-white
                      transition-all
                      duration-500

                      group-hover:translate-x-1
                      group-hover:rotate-45
                      group-hover:bg-primaryColor
                      group-hover:text-black

                      sm:h-5
                      sm:w-5

                      md:h-6
                      md:w-6

                      lg:h-7
                      lg:w-7
                    "
                  >
                    <ArrowUpRight
                      className="
                        h-2.5
                        w-2.5

                        sm:h-3
                        sm:w-3

                        md:h-3.5
                        md:w-3.5

                        lg:h-4
                        lg:w-4
                      "
                    />
                  </span>
                </span>
              </motion.button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* =======================================================
          CONTACT MODAL
      ======================================================== */}

      <ContactModal
        isOpen={isContactModalOpen}
        onClose={handleCloseContactModal}
      />
    </>
  );
}